import { withTranslation } from '@application/i18n/with_translation';
import { useReception } from '@application/use_reception';
import { Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useQuery } from '@tanstack/react-query';
import {
	DayOfWeek,
	demandManagementGetRuleSetOptions,
	type MonitoredHours,
	RuleSetType,
	type SpecialMonitoredHours,
} from '@visiba/api-vcs-client-v2';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';
import { DateDisplayFormats, useDate } from './use-date';

dayjs.extend(utc);
dayjs.extend(timezone);

interface RuleSetHours {
	IanaTimeZoneId: string | null;
	MonitoredHours: MonitoredHours[];
	SpecialMonitoredHours: SpecialMonitoredHours[];
}

const weekdays = [
	{ apiValue: DayOfWeek.Monday, translationKey: 'Common.Weekday.Monday' },
	{ apiValue: DayOfWeek.Tuesday, translationKey: 'Common.Weekday.Tuesday' },
	{ apiValue: DayOfWeek.Wednesday, translationKey: 'Common.Weekday.Wednesday' },
	{ apiValue: DayOfWeek.Thursday, translationKey: 'Common.Weekday.Thursday' },
	{ apiValue: DayOfWeek.Friday, translationKey: 'Common.Weekday.Friday' },
	{ apiValue: DayOfWeek.Saturday, translationKey: 'Common.Weekday.Saturday' },
	{ apiValue: DayOfWeek.Sunday, translationKey: 'Common.Weekday.Sunday' },
] as const;

const elements = {
	container: styled.section`
		width: min(100%, 248px);
		overflow: hidden;
		border-radius: ${tokens.borderRadius.medium};
		background: ${tokens.color.bg.surfaceMuted};
		box-shadow: ${tokens.elevation.skim};
	`,
	title: styled.div`
		padding: ${tokens.spacing.x1_5} ${tokens.spacing.x2};
		text-align: center;
		border-bottom: 1px solid ${tokens.color.border.default};
	`,
	list: styled.dl`
		margin: 0;
		padding: ${tokens.spacing.x0_5} 0;
	`,
	row: styled.div`
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		gap: ${tokens.spacing.x2};
		padding: ${tokens.spacing.x1} ${tokens.spacing.x2};
	`,
	day: styled.dt`
		margin: 0;
		text-align: left;
	`,
	time: styled.dd`
		margin: 0;
		text-align: right;
	`,
};

export function useOpeningHours(options: { type: RuleSetType; receptionId: number | null }) {
	return useQuery({
		...demandManagementGetRuleSetOptions({
			path: {
				receptionId: options.receptionId ?? 0,
			},
			query: {
				ruleSetType: options.type,
			},
		}),
		enabled: options.receptionId != null,
		select: (response): RuleSetHours | null => {
			const ruleSet = response.Data;

			if (ruleSet == null) {
				return null;
			}

			return {
				IanaTimeZoneId: ruleSet.IanaTimeZoneId,
				MonitoredHours: ruleSet.MonitoredHours,
				SpecialMonitoredHours: ruleSet.SpecialMonitoredHours,
			};
		},
	});
}

export function OpeningHours(options: { type: RuleSetType; receptionId: number | null }): JSX.Element | null {
	const { data } = useOpeningHours(options);
	const date = useDate();
	const ruleSet = data;

	if (ruleSet == null) {
		return null;
	}

	return (
		<>
			<elements.container aria-label={withTranslation('DropIn.OpeningHours.Title')}>
				<elements.title>
					<Text variant='labelSemiBold' color={tokens.color.content.muted}>
						{withTranslation('DropIn.OpeningHours.Title')}
					</Text>
				</elements.title>
				<elements.list>
					{groupByViewerWeekday(ruleSet.MonitoredHours, ruleSet.IanaTimeZoneId).map((intervals, index) => {
						const weekday = weekdays[index];

						return (
							<elements.row key={weekday.apiValue}>
								<elements.day>
									<Text variant='labelRegular' color={tokens.color.content.default}>
										{withTranslation(weekday.translationKey)}
									</Text>
								</elements.day>
								<elements.time>
									<Text
										variant='labelRegular'
										color={intervals.length > 0 ? tokens.color.content.default : tokens.color.content.muted}
									>
										{formatOpeningHours(intervals, date)}
									</Text>
								</elements.time>
							</elements.row>
						);
					})}
				</elements.list>
			</elements.container>
			{withTranslation('Case.OpeningHours.HavingIrregularHours')}
		</>
	);
}

interface ConvertedInterval {
	open: Date;
	close: Date;
	closed: boolean;
	allDay: boolean;
}

// dayOfWeekToMondayFirstIndex/dateNowToMondayFirstIndex give a locale-independent Monday-first
// index (0 = Monday ... 6 = Sunday) so the reception's schedule always lines up with the same
// weekday label regardless of the viewer's locale (which shifts native Date#getDay()/dayjs#day()
// week-start assumptions).
const dayOfWeekToMondayFirstIndex: Record<DayOfWeek, number> = {
	[DayOfWeek.Monday]: 0,
	[DayOfWeek.Tuesday]: 1,
	[DayOfWeek.Wednesday]: 2,
	[DayOfWeek.Thursday]: 3,
	[DayOfWeek.Friday]: 4,
	[DayOfWeek.Saturday]: 5,
	[DayOfWeek.Sunday]: 6,
};

function nativeDayToMondayFirstIndex(day: number): number {
	return (day + 6) % 7;
}

// Converts every reception-local interval into an absolute instant using an anchor date matching
// its own source weekday (rather than "today" for every entry), then groups the results by the
// weekday they land on in the viewer's own timezone. This avoids two bugs: intervals near
// midnight that cross into a different local day for the viewer being shown under the
// reception's original weekday, and every weekday being anchored to a date that may sit on the
// wrong side of a DST transition relative to its actual occurrence.
export function groupByViewerWeekday(monitoredHours: MonitoredHours[], ianaTimeZoneId: string | null): ConvertedInterval[][] {
	const buckets: ConvertedInterval[][] = [[], [], [], [], [], [], []];

	// RedRobin.Backend's RuleService only treats every day as open when MonitoredHours has no
	// entries at all; once any day has an entry, a day missing from the list is evaluated as
	// closed, so only synthesize all-day entries here when the whole list is empty. A present
	// entry with Close === null means open indefinitely from Open onward (unlike
	// SpecialMonitoredHours, where a missing Close means closed).
	const allHours =
		monitoredHours.length === 0
			? weekdays.map((weekday) => ({ hours: { DayOfWeek: weekday.apiValue, Open: '00:00:00', Close: '23:59:59' }, allDay: true }))
			: monitoredHours.map((hours) => ({ hours, allDay: hours.Close === null }));

	allHours.forEach(({ hours, allDay }) => {
		if (hours.DayOfWeek == null) {
			return;
		}

		const mondayFirstIndex = dayOfWeekToMondayFirstIndex[hours.DayOfWeek];
		const anchorDate = anchorDateForMondayFirstIndex(mondayFirstIndex);
		const open = toReceptionTimeAsAbsoluteInstant(hours.Open, ianaTimeZoneId, anchorDate);
		const close = toReceptionTimeAsAbsoluteInstant(allDay ? '23:59:59' : hours.Close, ianaTimeZoneId, anchorDate);
		const viewerIndex = nativeDayToMondayFirstIndex(open.getDay());

		buckets[viewerIndex].push({ open, close, closed: hours.Open === hours.Close, allDay });
	});

	buckets.forEach((bucket) => bucket.sort((a, b) => a.open.getTime() - b.open.getTime()));

	return buckets;
}

// Returns the date (this week) matching a Monday-first weekday index, computed in a
// locale-independent way (see nativeDayToMondayFirstIndex).
function anchorDateForMondayFirstIndex(mondayFirstIndex: number): dayjs.Dayjs {
	const today = dayjs();
	const todayIndex = nativeDayToMondayFirstIndex(today.day());

	return today.add(mondayFirstIndex - todayIndex, 'day');
}

function formatOpeningHours(intervals: ConvertedInterval[], date: ReturnType<typeof useDate>): string {
	if (intervals.length === 0) {
		return withTranslation('DropIn.OpeningHours.Hours.Closed');
	}

	const timeFormat = date.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.JUST_TIME, { includeTimePrefix: false });

	return intervals
		.map((interval) => {
			if (interval.closed) {
				return withTranslation('DropIn.OpeningHours.Hours.Closed');
			}

			if (interval.allDay) {
				return withTranslation('DropIn.OpeningHours.Hours.Open');
			}

			const open = date.newDate(interval.open).format(timeFormat);
			const close = date.newDate(interval.close).format(timeFormat);

			return withTranslation('Common.Date.Range', { from: open, to: close });
		})
		.join(', ');
}

// Converts a reception-local wall-clock time (e.g. "08:00:00") into the absolute instant it
// represents on the given anchor date, using the reception's own IANA timezone. Formatting the
// resulting Date with dayjs (browser-local by default) then mirrors how the home-screen buttons
// already display DemandManagementStatus.NextEvent: an absolute instant rendered in the viewer's
// own timezone, rather than the reception's wall-clock digits reinterpreted verbatim.
function toReceptionTimeAsAbsoluteInstant(
	time: string | null | undefined,
	ianaTimeZoneId: string | null,
	anchorDate: dayjs.Dayjs,
): Date {
	const [hours = 0, minutes = 0, seconds = 0] = time?.split(':').map(Number) ?? [];

	if (ianaTimeZoneId == null) {
		return new Date(1970, 0, 1, hours, minutes, seconds);
	}

	return dayjs.tz(anchorDate.format('YYYY-MM-DD'), ianaTimeZoneId).hour(hours).minute(minutes).second(seconds).toDate();
}
